import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  SxProps,
  Tooltip,
} from '@mui/material';
import UserAnalyticsTable from './UserAnalyticsTable';
import { analyticsOuterBoxStyles } from './DistributionAnalyticsTable';
import ViewRecipientsDialog, { loaderSx } from './ViewRecipientsDialog';
import { useParams } from 'react-router-dom';
import { getMandatoryReplyMessages } from '../api/requests/users';
import { GridColDef } from '@mui/x-data-grid';
import { formatFullDateTime } from '../utils/utils';
import { sectionDescriptionSx, sectionTitleSx } from './UserAnalytics';
import { PaginationType } from './Groups';
import { viewReciepientStyles } from './MessageStatsTable';

interface MandatoryRlyMessageType {
  createdOn: string;
  groupName: string;
  isGroup: boolean;
  messageId: number;
  messageText: string;
  recipientsCount: number;
  viewedCount: number;
}

interface RowsType {
  id: number;
  text: string;
  created: string;
  recipientsCount: number;
  viewedCount: number;
  groupName: string;
  isGroup: boolean;
}

const MandatoryRepliesTable = () => {
  const { userId } = useParams();
  const [rows, setRows] = useState<RowsType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<{
    id: number;
  } | null>(null);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 5,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);

  const viewDetailsHandler = (id: number) => {
    setSelectedRow({ id });
  };

  const closeDialog = () => {
    setSelectedRow(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'text',
      headerName: 'Text',
      flex: 1.3,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography fontSize='14px' sx={textSx}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'created',
      headerName: 'Created',
      flex: 1,
      renderCell: (params) => (
        <Typography fontSize='14px'>{params.value}</Typography>
      ),
    },
    {
      field: 'recipientsCount',
      headerName: 'Recipient',
      flex: 1.2,
      renderCell: (params) => (
        <Typography
          fontSize='14px'
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {params.value}{' '}
          <Tooltip
            title={params.row.isGroup ? params.row.groupName : 'Individual'}
          >
            <Box component={'span'} sx={groupTextSx}>
              {params.row.isGroup ? params.row.groupName : 'individual'}
            </Box>
          </Tooltip>
        </Typography>
      ),
    },
    {
      field: 'viewedCount',
      headerName: 'Viewed',
      flex: 0.5,
      renderCell: (params) => (
        <Typography fontSize='14px'>{params.value}</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      disableColumnMenu: true,
      width: 180,
      renderCell: (params) => (
        <Typography
          sx={viewReciepientStyles}
          onClick={() => viewDetailsHandler(params.row.id)}
        >
          View Recipients
        </Typography>
      ),
    },
  ];
  const fetchMandatoryReplyMessages = async () => {
    setLoading(true);
    try {
      const response =
        userId &&
        (await getMandatoryReplyMessages({
          userId,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }));
      if (response && response.rows) {
        const updatedResponse = response.rows.map(
          (item: MandatoryRlyMessageType) => ({
            id: item.messageId,
            text: item.messageText || '--',
            created: formatFullDateTime(item.createdOn) || '--',
            recipientsCount: item.recipientsCount || 0,
            viewedCount: item.viewedCount || 0,
            groupName: item.groupName || '--',
            isGroup: item.isGroup,
          })
        );
        setRows(updatedResponse);
        setTotalRowsCount(response.totalCount);
      } else {
        setRows([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch mandatory messages', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMandatoryReplyMessages();
  }, [paginationModel]);

  return (
    <Box sx={analyticsOuterBoxStyles}>
      <Typography sx={sectionTitleSx}>Mandatory Messages</Typography>
      <Typography sx={sectionDescriptionSx}>
        Messages that require acknowledgment or response
      </Typography>
      {loading ? (
        <Box sx={loaderSx}>
          <CircularProgress size={60} thickness={6} />
        </Box>
      ) : rows.length > 0 ? (
        <UserAnalyticsTable
          columns={columns}
          rows={rows}
          sx={{ height: '100%', width: '100%' }}
          setPaginationModel={setPaginationModel}
          paginationModel={paginationModel}
          totalRowsCount={totalRowsCount}
        />
      ) : (
        <Box sx={loaderSx}>
          <Typography variant='body2' color='textSecondary'>
            No data found.
          </Typography>
        </Box>
      )}
      {selectedRow && (
        <ViewRecipientsDialog
          open={!!selectedRow}
          handleClose={closeDialog}
          id={selectedRow.id}
          title='Mandatory Reply Message Recipients'
          description='List of all recipients and their interaction status'
          section='Mandatory Replies'
        />
      )}
    </Box>
  );
};

export default MandatoryRepliesTable;

const groupTextSx: SxProps = {
  display: 'inline-block',
  border: '1px solid #dedede',
  borderRadius: '10px',
  marginLeft: '10px',
  p: 0.5,
  color: '#2f2f2f',
  maxWidth: '100px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const textSx: SxProps = {
  maxWidth: '220px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
